/**
 * Proposed actions under an answer: what would change, with Bevestigen /
 * Weigeren. Nothing runs without a click; a confirmed action can be undone.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  FilePlus2,
  FolderInput,
  ListPlus,
  Loader2,
  PenLine,
  SquarePen,
  Undo2,
  X,
} from 'lucide-react';
import { useStore } from '../db/context';
import { cn } from '../lib/cn';
import type { ActionPreview } from '@nexus/ai';
import { useAiChat, type ActionCard } from './chat';
import { useDescribe } from './describe';

const ICONS = {
  create_task: ListPlus,
  update_task: SquarePen,
  create_page: FilePlus2,
  append_to_page: PenLine,
  move_page: FolderInput,
} as const;

export function ActionCards({ cards }: { cards: ActionCard[] }) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const describe = useDescribe();
  const decide = useAiChat((s) => s.decide);
  const pending = cards.filter((c) => c.status === 'pending');
  const all = (confirm: boolean) => {
    // One after the other, so the outcomes keep their order.
    void pending.reduce<Promise<void>>(
      (p, c) => p.then(() => decide(store, c.callId, confirm, describe, i18n.language)),
      Promise.resolve(),
    );
  };

  return (
    <div className="flex flex-col gap-2" role="group" aria-label={t('ai.actions.label')}>
      {cards.map((c) => (
        <Card key={c.callId} card={c} />
      ))}
      {pending.length > 1 && (
        <div className="flex justify-end gap-1">
          <button
            type="button"
            onClick={() => all(false)}
            className="rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover"
          >
            {t('ai.actions.declineAll')}
          </button>
          <button
            type="button"
            onClick={() => all(true)}
            className="rounded-full bg-accent px-2 py-1 text-xs font-medium text-accent-fg"
          >
            {t('ai.actions.confirmAll', { count: pending.length })}
          </button>
        </div>
      )}
    </div>
  );
}

/** What an action would change (also used to confirm requests from Claude Desktop). */
export function ActionPreviewView({ preview }: { preview: ActionPreview }) {
  const { t } = useTranslation();
  const Icon = ICONS[preview.action];
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 size-4 shrink-0 text-accent-ink" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-fg-muted">{t(`ai.actions.kind.${preview.action}`)}</p>
        <p className="font-medium break-words">{preview.title}</p>
        {preview.details.length > 0 && (
          <dl className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-2 text-xs">
            {preview.details.map((d) => (
              <div key={d.label} className="contents">
                <dt className="text-fg-muted">{t(`ai.actions.detail.${d.label}`)}</dt>
                <dd className="break-words">{d.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {preview.markdown && (
          <pre className="mt-1 max-h-32 overflow-y-auto rounded bg-bg-subtle p-1.5 text-xs whitespace-pre-wrap">
            {preview.markdown}
          </pre>
        )}
      </div>
    </div>
  );
}

function Card({ card }: { card: ActionCard }) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const describe = useDescribe();
  const { decide, undo } = useAiChat();
  const [undoFailed, setUndoFailed] = useState(false);
  const { preview, status } = card;

  return (
    <div
      data-testid="ai-action"
      className={cn(
        'flex flex-col gap-1.5 rounded-lg border p-2',
        status === 'pending' ? 'border-accent/50 bg-accent/5' : 'border-border',
      )}
    >
      <ActionPreviewView preview={preview} />

      {status === 'pending' ? (
        <div className="flex justify-end gap-1">
          <button
            type="button"
            onClick={() => void decide(store, card.callId, false, describe, i18n.language)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover"
          >
            <X className="size-3" />
            {t('ai.actions.decline')}
          </button>
          <button
            type="button"
            onClick={() => void decide(store, card.callId, true, describe, i18n.language)}
            className="flex items-center gap-1 rounded-full bg-accent px-2 py-1 text-xs font-medium text-accent-fg"
          >
            <Check className="size-3" />
            {t('ai.actions.confirm')}
          </button>
        </div>
      ) : (
        <p
          className={cn(
            'flex items-center gap-1.5 text-xs',
            status === 'failed' ? 'text-danger' : 'text-fg-muted',
          )}
          role={status === 'failed' ? 'alert' : undefined}
        >
          {status === 'running' && <Loader2 className="size-3 animate-spin" />}
          {status === 'done' && <Check className="size-3 text-green-600" />}
          <span className="flex-1">
            {t(`ai.actions.status.${status}`)}
            {status === 'failed' && card.error ? `: ${card.error}` : ''}
            {undoFailed ? ` · ${t('ai.actions.undoFailed')}` : ''}
          </span>
          {status === 'done' && (
            <button
              type="button"
              onClick={async () => setUndoFailed(!(await undo(store, card.callId)))}
              className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-bg-hover hover:text-fg"
            >
              <Undo2 className="size-3" />
              {t('ai.actions.undo')}
            </button>
          )}
        </p>
      )}
    </div>
  );
}
